import type { FiCountry } from '../contexts/LocaleContext';
import type * as types from '../types';
import { cartItemToSegmentProducts } from './analytics/events';
import { promoUnitPriceInCents } from './cart';
import { logInternalError } from './errors';
import { isAxiosError, webApiClient } from './fi-api/apiUtils';
import { centsToDollarsNumber } from './util/money';

/**
 * Tells the backend a shopper typed a valid email at checkout, so Customer.io can run the
 * abandoned-cart email if no order follows. Fire-and-forget: nothing here is shown to the shopper.
 */

const LAST_SENT_STORAGE_KEY = 'checkoutEmailCaptured.lastSent';

export interface ActivePromotionSummary {
  couponCode?: string;
  end?: Date;
  extendedFrom?: Date;
}

export interface CheckoutEmailCapturedInput {
  email: string;
  cart: types.Cart;
  cartPricing: types.CartPricing;
  country: FiCountry;
  currency: types.FiCurrency;
  activePromotion: ActivePromotionSummary;
}

interface CheckoutEmailCapturedProduct {
  sku: string;
  quantity: number;
  unitPrice: number;
  promoPrice?: number;
}

export interface CheckoutEmailCapturedPayload {
  email: string;
  country: FiCountry;
  currency: types.FiCurrency;
  products: CheckoutEmailCapturedProduct[];
  couponCode?: string;
  promoPercentOff?: number;
  promoEndsAt?: string;
  cartTotal: number;
  cartTotalBeforePromo?: number;
}

/**
 * Hardware line items only: the email shows trackers, and the backend resolves each SKU against
 * the hardware catalog. Memberships and gift cards are left out.
 */
function hardwareProducts(
  cart: types.Cart,
  cartPricing: types.CartPricing,
  currency: string,
): CheckoutEmailCapturedProduct[] {
  return Object.values(cart.cartItems).flatMap((cartItem) => {
    const hardwareDiscount = cartPricing.hardwareDiscounts?.[cartItem.cartItemId];
    return cartItemToSegmentProducts(cartItem, currency, hardwareDiscount)
      .filter((product) => product.subscriptionRenewalMonths === undefined && !('giftCardDelivery' in cartItem))
      .map((product) => {
        const repriced = hardwareDiscount && product.sku === cartItem.lineItem.sku;
        const promoPriceInCents = repriced
          ? hardwareDiscount.discountedPriceInCents
          : cartPricing.couponDiscount
            ? promoUnitPriceInCents(cartItem, Math.round(product.price * 100), cartPricing)
            : undefined;
        const promoPrice = promoPriceInCents === undefined ? undefined : centsToDollarsNumber(promoPriceInCents);
        return { sku: product.sku, quantity: cartItem.quantity, unitPrice: product.price, promoPrice };
      });
  });
}

/** Undefined when the cart holds nothing the email could show. */
export function buildCheckoutEmailCapturedPayload(
  input: CheckoutEmailCapturedInput,
): CheckoutEmailCapturedPayload | undefined {
  const { email, cart, cartPricing, country, currency, activePromotion } = input;
  const products = hardwareProducts(cart, cartPricing, currency);
  if (products.length === 0) {
    return undefined;
  }

  // The cart's own code wins; otherwise pitch the live promotion so the email's link applies it.
  const couponCode = cart.couponCode || activePromotion.couponCode || undefined;
  const promoIsLive = couponCode !== undefined && couponCode === activePromotion.couponCode;
  const { totalInCents, appliedDiscountInCents } = cartPricing;

  return {
    email,
    country,
    currency,
    products,
    couponCode,
    promoPercentOff: cartPricing.couponDiscount?.type === 'percent' ? cartPricing.couponDiscount.percent : undefined,
    promoEndsAt: promoIsLive ? activePromotion.end?.toISOString() : undefined,
    cartTotal: centsToDollarsNumber(totalInCents),
    cartTotalBeforePromo:
      appliedDiscountInCents > 0 ? centsToDollarsNumber(totalInCents + appliedDiscountInCents) : undefined,
  };
}

function fingerprint(payload: CheckoutEmailCapturedPayload) {
  const items = payload.products.map((product) => `${product.sku}x${product.quantity}`);
  return [payload.email, payload.couponCode ?? '', ...items].join('|');
}

function readLastSent(): string | null {
  try {
    return sessionStorage.getItem(LAST_SENT_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeLastSent(value: string) {
  try {
    sessionStorage.setItem(LAST_SENT_STORAGE_KEY, value);
  } catch {
    // storage unavailable: worst case the backend sees a repeat, which it rate-limits
  }
}

/** Sends once per (email, cart contents, coupon) for the tab's lifetime. Never throws. */
export async function reportCheckoutEmailCaptured(input: CheckoutEmailCapturedInput): Promise<void> {
  const payload = buildCheckoutEmailCapturedPayload(input);
  if (!payload) {
    return;
  }
  const key = fingerprint(payload);
  if (readLastSent() === key) {
    return;
  }

  try {
    await webApiClient.post('/api/ecommerce/checkout-email-captured', payload);
    writeLastSent(key);
  } catch (err) {
    // A 4xx is the backend declining (rate limit, country not launched, unknown SKU): nothing to
    // retry and nothing to alert on. Anything else means the endpoint is failing and we want to know.
    if (isAxiosError(err) && err.response && err.response.status < 500) {
      return;
    }
    logInternalError(err instanceof Error ? err : new Error(String(err)), {
      tags: { checkoutStep: 'checkoutEmailCaptured' },
    });
  }
}
